import { Breakpoint } from "@mui/material/styles";

/**
 * Base grid size in pixels.
 * Used for consistent spacing throughout the design system.
 *
 * TODO: This should be a theme value, not a constant - but we don't want to break existing code that might rely on the MUI theme default value
 */
export const GRID_SIZE = 4;

/**
 * Helper type for responsive properties that can vary by breakpoint.
 * Accepts either a single value or an object with breakpoint-specific values.
 *
 * @template T - The type of value for each breakpoint
 *
 * @example
 * // Single value
 * const spacing: ResponsiveValue<number> = 2;
 *
 * @example
 * // Responsive object with breakpoint-specific values
 * const spacing: ResponsiveValue<number> = { xs: 1, md: 2, lg: 4 };
 *
 * @example
 * // Use in a component prop
 * interface MyComponentProps {
 *   spacing?: ResponsiveValue<number>;
 * }
 */
export type ResponsiveValue<T> = T | Partial<Record<Breakpoint, T>>;

/**
 * Multiplies spacing values by GRID_SIZE (4px)
 *
 * @param spacing - A number or responsive object with numeric values
 * @returns The spacing multiplied by GRID_SIZE
 *
 * @example
 * multiplySpacing(2) // Returns 8 (2 * 4)
 * multiplySpacing({ xs: 1, md: 4 }) // Returns { xs: 4, md: 16 }
 */
export const multiplySpacing = (
  spacing: ResponsiveValue<number> | undefined,
): ResponsiveValue<number> | undefined => {
  if (spacing === undefined) {
    return undefined;
  }

  if (typeof spacing === "number") {
    return spacing * GRID_SIZE;
  }

  // Handle responsive spacing object
  const result: ResponsiveValue<number> = {};
  for (const [key, value] of Object.entries(spacing)) {
    if (value !== undefined) {
      result[key as Breakpoint] = value * GRID_SIZE;
    }
  }
  return result;
};
